import { Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { AuthError, AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FaroObservabilityService } from '@hub-services/faro-observability.service';
import { AuthOutcome, classifyAuthError } from './auth-error-classifier';
import { truncateCodePoints } from './unicode-truncate';

const FAILURE_EVENT_MAP = new Map<EventType, { faroName: string; label: string }>([
  [EventType.LOGIN_FAILURE,         { faroName: 'msal_login_failure',         label: 'LOGIN_FAILURE' }],
  [EventType.ACQUIRE_TOKEN_FAILURE, { faroName: 'msal_acquire_token_failure', label: 'ACQUIRE_TOKEN_FAILURE' }],
  [EventType.SSO_SILENT_FAILURE,    { faroName: 'msal_sso_silent_failure',    label: 'SSO_SILENT_FAILURE' }],
]);

// Forwards MSAL broadcast events (success / login_failure / acquire_token_failure
// / sso_silent_failure) into Faro so we can correlate browser auth state with
// backend traces.
//
// Deliberately separate from AuthService — that service centralizes MSAL
// *interaction* (state, redirects, login/logout). MsalTelemetryService is
// pure observability and never mutates auth state. Both subscribe to
// MsalBroadcastService.msalSubject$ independently; observability is decoupled
// from interaction so changes to one don't perturb the other.
//
// Faro events include the same classifyAuthError outcome (kind / reason /
// errorCode) that AuthService uses to decide its action, keeping the
// observability semantics aligned with what the app actually did.
//
// Activated via APP_INITIALIZER in app.module so subscriptions start before
// any component or guard fires MSAL work.
@Injectable({ providedIn: 'root' })
export class MsalTelemetryService implements OnDestroy {
  private readonly destroying$ = new Subject<void>();
  private started = false;
  private subscription: Subscription | null = null;

  private static readonly MAX_MESSAGE_LENGTH = 256;

  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private faro: FaroObservabilityService,
  ) {}

  // Idempotent — calling start() twice does not double-subscribe.
  start(): void {
    if (this.started) return;
    this.started = true;

    this.subscription = this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this.destroying$))
      .subscribe((message: EventMessage) => {
        this.handle(message);
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private handle(message: EventMessage): void {
    switch (message.eventType) {
      case EventType.LOGIN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
      case EventType.SSO_SILENT_SUCCESS:
        this.recordSuccess(message);
        return;
      default: {
        const failure = FAILURE_EVENT_MAP.get(message.eventType);
        if (failure) {
          this.recordFailure(message, failure);
        }
        // Other MSAL lifecycle events are ignored — too noisy for Faro.
      }
    }
  }

  private recordSuccess(message: EventMessage): void {
    const payload = (message.payload as AuthenticationResult | null) || null;
    this.faro.pushEvent('msal_success', {
      'msal.event_type': String(message.eventType),
      'msal.scopes': (payload?.scopes || []).join(','),
      'msal.has_id_token': String(!!payload?.idToken),
      'msal.account_environment': payload?.account?.environment || '',
    });
  }

  private recordFailure(message: EventMessage, mapping: { faroName: string; label: string }): void {
    const error = message.error;
    if (!(error instanceof AuthError)) return;

    const outcome: AuthOutcome = classifyAuthError(error);

    this.faro.pushEvent(mapping.faroName, {
      'msal.event_type': String(message.eventType),
      'msal.error_code': error.errorCode || '',
      // Code-point-safe truncation: error messages from B2C/AAD can localize
      // and contain CJK / emoji that would corrupt under raw slice().
      'msal.error_message': truncateCodePoints(error.message || '', MsalTelemetryService.MAX_MESSAGE_LENGTH),
      'msal.error_class': error.constructor.name,
      // Classifier outcome: matches the kind that AuthService uses to decide
      // its next action (reauth / logout_then_reauth / denied / config_error /
      // cancelled). Lets a Faro query group failures by intended response.
      'auth.outcome_kind': outcome.kind,
      'auth.outcome_reason': outcome.reason,
      ...(outcome.errorCode ? { 'auth.outcome_error_code': outcome.errorCode } : {}),
    });
    this.faro.pushError(error, {
      'msal.event': mapping.label,
      'msal.error_code': error.errorCode || '',
      'auth.outcome_kind': outcome.kind,
    });
  }
}

// APP_INITIALIZER factory: starts the subscription so events are captured
// from the very first MSAL broadcast.
export function initializeMsalTelemetry(svc: MsalTelemetryService) {
  return () => {
    svc.start();
  };
}
